import { ref, computed, watch } from 'vue'
import { defineStore } from 'pinia'

type Theme = 'light' | 'dark' | 'system'

export const useThemeStore = defineStore('theme', () => {
  // Estado
  const currentTheme = ref<Theme>('system')
  const isDarkMode = ref(false)

  // Getters
  const theme = computed(() => currentTheme.value)
  const isSystemTheme = computed(() => currentTheme.value === 'system')
  const isLightTheme = computed(() => currentTheme.value === 'light')
  const isDarkTheme = computed(() => currentTheme.value === 'dark')

  // Detectar preferência do sistema
  const getSystemTheme = (): 'light' | 'dark' => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    }
    return 'light'
  }

  // Aplicar tema ao DOM
  const applyTheme = (theme: 'light' | 'dark') => {
    if (typeof document !== 'undefined') {
      const root = document.documentElement
      
      if (theme === 'dark') {
        root.classList.add('dark')
        isDarkMode.value = true
      } else {
        root.classList.remove('dark')
        isDarkMode.value = false
      }
    }
  }

  // Atualizar tema baseado na configuração atual
  const updateTheme = () => {
    let effectiveTheme: 'light' | 'dark'
    
    if (currentTheme.value === 'system') {
      effectiveTheme = getSystemTheme()
    } else {
      effectiveTheme = currentTheme.value
    }
    
    applyTheme(effectiveTheme)
  }

  // Actions
  const setTheme = (theme: Theme) => {
    currentTheme.value = theme
    localStorage.setItem('theme', theme)
    updateTheme()
  }

  const toggleTheme = () => {
    if (currentTheme.value === 'light') {
      setTheme('dark')
    } else if (currentTheme.value === 'dark') {
      setTheme('system')
    } else {
      setTheme('light')
    }
  }

  const initializeTheme = () => {
    // Carregar tema salvo ou usar padrão
    const savedTheme = localStorage.getItem('theme') as Theme
    if (savedTheme && ['light', 'dark', 'system'].includes(savedTheme)) {
      currentTheme.value = savedTheme
    }
    
    updateTheme()
    
    // Escutar mudanças na preferência do sistema
    if (typeof window !== 'undefined' && window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      
      const handleSystemThemeChange = () => {
        if (currentTheme.value === 'system') {
          updateTheme()
        }
      }
      
      // Adicionar listener para mudanças
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleSystemThemeChange)
      } else {
        // Fallback para navegadores mais antigos
        mediaQuery.addListener(handleSystemThemeChange)
      }
    }
  }

  // Watcher para mudanças no tema
  watch(currentTheme, () => {
    updateTheme()
  })

  return {
    // Estado
    currentTheme,
    isDarkMode,
    
    // Getters
    theme,
    isSystemTheme,
    isLightTheme,
    isDarkTheme,
    
    // Actions
    setTheme,
    toggleTheme,
    initializeTheme,
    updateTheme
  }
})